/**
 * Assegnazione di un ruolo di squadra, da due lati: dall'account si sceglie
 * la squadra-stagione (V51), dalla squadra si sceglie l'account (V44). Il
 * ruolo e sempre Mister o Dirigente: l'Admin Squadra non si assegna qui.
 */

import { type FormEvent, useState } from 'react';
import { OctagonXIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { messaggio } from '../comuni/dati.js';
import type { RuoloAssegnabile } from './ruoli.js';

export interface GruppoOpzioni {
  titolo: string | null;
  opzioni: { valore: string; etichetta: string }[];
}

export function FormRuolo({
  titolo,
  descrizione,
  etichettaScelta,
  gruppi,
  onChiudi,
  onInvia,
}: {
  titolo: string;
  descrizione: string;
  etichettaScelta: string;
  gruppi: GruppoOpzioni[];
  onChiudi: () => void;
  /** Rifiuta con l'errore del server, che il form mostra. */
  onInvia: (ruolo: RuoloAssegnabile, scelta: string) => Promise<void>;
}) {
  const [ruolo, setRuolo] = useState<RuoloAssegnabile>('mister');
  const [scelta, setScelta] = useState('');
  const [erroreScelta, setErroreScelta] = useState<string | null>(null);
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    if (!scelta) return setErroreScelta(`Scegli ${etichettaScelta.toLowerCase()}.`);
    setErroreScelta(null);
    setErroreServer(null);
    setInCorso(true);
    try {
      await onInvia(ruolo, scelta);
      toast.success('Ruolo assegnato.');
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>{titolo}</DialogTitle>
        <DialogDescription>{descrizione}</DialogDescription>
      </DialogHeader>

      <FieldGroup>
        <Field>
          <FieldLabel>Ruolo</FieldLabel>
          <ToggleGroup
            type="single"
            variant="outline"
            value={ruolo}
            onValueChange={(v) => v && setRuolo(v as RuoloAssegnabile)}
          >
            <ToggleGroupItem value="mister">Mister</ToggleGroupItem>
            <ToggleGroupItem value="dirigente">Dirigente</ToggleGroupItem>
          </ToggleGroup>
          <FieldDescription>
            {ruolo === 'mister'
              ? 'Gestisce attività, partite e valutazioni della squadra.'
              : 'Come il Mister, ma senza voti e attributi.'}
          </FieldDescription>
        </Field>

        <Field data-invalid={Boolean(erroreScelta)}>
          <FieldLabel htmlFor="ruolo-scelta">{etichettaScelta}</FieldLabel>
          <Select value={scelta} onValueChange={setScelta}>
            <SelectTrigger
              id="ruolo-scelta"
              className="w-full"
              aria-invalid={Boolean(erroreScelta)}
            >
              <SelectValue placeholder="Scegli…" />
            </SelectTrigger>
            <SelectContent>
              {gruppi.map((g) => (
                <SelectGroup key={g.titolo ?? ''}>
                  {g.titolo && <SelectLabel>{g.titolo}</SelectLabel>}
                  {g.opzioni.map((o) => (
                    <SelectItem key={o.valore} value={o.valore}>
                      {o.etichetta}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          <FieldError>{erroreScelta}</FieldError>
        </Field>
      </FieldGroup>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          Assegna
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
